import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Eye, EyeOff, Loader2, Plus, X, MailCheck } from "lucide-react";
import toast from "react-hot-toast";
import apiService from "../../services/api";
import tokenManager from "../../utils/tokenManager";
import AuthShell, {
  Field,
  FormError,
  inputClass,
  primaryButtonClass,
} from "./AuthShell";

// Company signup, the same 4 steps as the mobile app:
//   1. Admin account   2. Company   3. Leave defaults   4. Invite the team
// Creates the company with POST /auth/register, which also signs the person
// in as its admin, then saves the leave defaults.

const STEPS = 4;
const MAX_INVITES = 10;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TEAM_SIZES = ["1-10", "11-50", "51-200", "201-500", "501+"];

// Where the company is billed. Picks the currency and tax, and the local
// defaults for leave (each one can be changed later in Settings).
const BILLING_MARKETS = [
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "ZA", name: "South Africa" },
  { code: "OTHER", name: "Another country (billed in GBP)" },
];

const AU_STATES = [
  { code: "AU-ACT", name: "Australian Capital Territory" },
  { code: "AU-NSW", name: "New South Wales" },
  { code: "AU-NT", name: "Northern Territory" },
  { code: "AU-QLD", name: "Queensland" },
  { code: "AU-SA", name: "South Australia" },
  { code: "AU-TAS", name: "Tasmania" },
  { code: "AU-VIC", name: "Victoria" },
  { code: "AU-WA", name: "Western Australia" },
];

// Signup's "Leave defaults" step, pre-filled for the market
const MARKET_LEAVE_DEFAULTS = {
  GB: { annualDays: 25, startMonth: 1, carryOver: 5 },
  AU: { annualDays: 20, startMonth: 7, carryOver: 0 },
  ZA: { annualDays: 15, startMonth: 3, carryOver: 0 },
};

const MARKET_LEAVE_NOTES = {
  GB: "Bank holidays are given on top and don't come out of the allowance.",
  AU: "Annual leave accrues as people work — 4 weeks a year for full-time staff — and unused leave carries over. Public holidays never come off the balance.",
  ZA: "Each person gets a 12-month leave cycle from their start date, accruing monthly, and unused leave carries over. Public holidays never come off the balance.",
};

const marketFromCountry = (code) => (MARKET_LEAVE_DEFAULTS[code] ? code : "OTHER");

// ?ref=CODE or ?partner=CODE on the signup link — or on any earlier page in
// this browser in the last 90 days (App.js remembers it), so a partner keeps
// the credit when the visitor lands on another page first
const referralFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get("ref") || params.get("partner") || "").trim().toUpperCase();
    if (fromUrl) return fromUrl;
    const saved = JSON.parse(localStorage.getItem("clockout_referral") || "null");
    if (saved?.code && Date.now() - saved.at < 90 * 24 * 60 * 60 * 1000) return String(saved.code).toUpperCase();
    return "";
  } catch {
    return "";
  }
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Used if the country list can't be loaded before sign-in. The full list
// comes from GET /holiday-locations/countries when it's reachable.
const FALLBACK_COUNTRIES = [
  ["AE", "United Arab Emirates"], ["AU", "Australia"], ["BE", "Belgium"], ["BR", "Brazil"],
  ["CA", "Canada"], ["CH", "Switzerland"], ["DE", "Germany"], ["DK", "Denmark"],
  ["ES", "Spain"], ["FI", "Finland"], ["FR", "France"], ["GB", "United Kingdom"],
  ["IE", "Ireland"], ["IN", "India"], ["IT", "Italy"], ["JP", "Japan"],
  ["KE", "Kenya"], ["MX", "Mexico"], ["NG", "Nigeria"], ["NL", "Netherlands"],
  ["NO", "Norway"], ["NZ", "New Zealand"], ["PH", "Philippines"], ["PK", "Pakistan"],
  ["PL", "Poland"], ["PT", "Portugal"], ["SE", "Sweden"], ["SG", "Singapore"],
  ["US", "United States"], ["ZA", "South Africa"],
].map(([code, name]) => ({ code, name }));

const FALLBACK_REGIONS = {
  GB: [
    { code: "GB-ENG", name: "England and Wales" },
    { code: "GB-SCT", name: "Scotland" },
    { code: "GB-NIR", name: "Northern Ireland" },
  ],
};

// Holidays imported for the new company: this year and next
const HOLIDAY_YEARS = [new Date().getFullYear(), new Date().getFullYear() + 1];

const unwrapList = (res, key) => {
  if (Array.isArray(res)) return res;
  const d = res?.data ?? res;
  if (Array.isArray(d)) return d;
  if (key && Array.isArray(d?.[key])) return d[key];
  return [];
};

const flagEmoji = (code = "") =>
  /^[A-Z]{2}$/i.test(code)
    ? String.fromCodePoint(...code.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)))
    : "🏳️";

// Best guess at the person's country from the browser (en-GB → GB)
const guessCountry = () => {
  try {
    const tag = navigator.language || "";
    const part = tag.split("-")[1];
    return part && /^[A-Z]{2}$/i.test(part) ? part.toUpperCase() : "GB";
  } catch {
    return "GB";
  }
};

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "An uppercase and a lowercase letter", test: (p) => /[a-z]/.test(p) && /[A-Z]/.test(p) },
  { label: "A number", test: (p) => /\d/.test(p) },
];

const TITLES = [
  { title: "Create your admin account", subtitle: "You'll manage leave for your company." },
  { title: "Your company", subtitle: "Tell us who you're setting this up for." },
  { title: "Leave defaults", subtitle: "Starting values for everyone. You can change them later in Settings." },
  { title: "Invite your team", subtitle: "Add colleagues by email, or skip this and do it later." },
];

const clamp = (value, min, max) => {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
};

const NumberField = ({ label, hint, value, min, max, onChange, disabled }) => (
  <Field label={label} hint={hint}>
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={(e) => onChange(clamp(e.target.value, min, max))}
      className={inputClass}
      disabled={disabled}
    />
  </Field>
);

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  const [form, setForm] = useState(() => {
    const country = guessCountry();
    const market = marketFromCountry(country);
    const defaults = MARKET_LEAVE_DEFAULTS[market] ?? MARKET_LEAVE_DEFAULTS.GB;
    return {
      name: "",
      email: "",
      password: "",
      companyName: "",
      teamSize: "",
      billingMarket: market,
      billingRegion: "",
      referralCode: referralFromUrl(),
      annualDays: defaults.annualDays,
      workingDays: 5,
      carryOver: defaults.carryOver,
      startMonth: defaults.startMonth,
      holidays: true,
      holidayCountry: country,
      holidayRegion: "",
      invites: [""],
    };
  });

  // Choosing where the company is billed also sets its public-holiday
  // country and the local leave defaults
  const setMarket = (market) => {
    setError("");
    setForm((prev) => {
      const defaults = MARKET_LEAVE_DEFAULTS[market];
      return {
        ...prev,
        billingMarket: market,
        billingRegion: "",
        ...(defaults && {
          annualDays: defaults.annualDays,
          startMonth: defaults.startMonth,
          carryOver: defaults.carryOver,
          holidayCountry: market,
        }),
      };
    });
  };

  const set = (key, value) => {
    setError("");
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ── Public holiday countries / regions ──
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [regions, setRegions] = useState(FALLBACK_REGIONS[form.holidayCountry] ?? []);
  const [loadingRegions, setLoadingRegions] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getHolidayCountries()
      .then((res) => {
        const list = unwrapList(res, "countries").filter((c) => c?.code && c?.name);
        if (!cancelled && list.length) {
          setCountries([...list].sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch(() => { /* keep the built-in list */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const code = form.holidayCountry;
    let cancelled = false;
    const fallback = FALLBACK_REGIONS[code] ?? [];
    setRegions(fallback);
    setForm((prev) => ({
      ...prev,
      holidayRegion: code === "GB" ? "GB-ENG" : code === "AU" && prev.billingMarket === "AU" ? prev.billingRegion : "",
    }));
    if (!code) return undefined;
    setLoadingRegions(true);
    apiService
      .getHolidayCountryRegions(code)
      .then((res) => {
        const list = unwrapList(res, "regions").filter((r) => r?.code && r?.name);
        if (cancelled) return;
        if (list.length) {
          setRegions(list);
          if (code === "GB" && list.some((r) => r.code === "GB-ENG")) {
            setForm((prev) => ({ ...prev, holidayRegion: "GB-ENG" }));
          }
        }
      })
      .catch(() => { /* regions are optional */ })
      .finally(() => { if (!cancelled) setLoadingRegions(false); });
    return () => { cancelled = true; };
  }, [form.holidayCountry]);

  // If the saved country isn't in the list (unusual locale), fall back to the UK
  useEffect(() => {
    if (countries.length && !countries.some((c) => c.code === form.holidayCountry)) {
      setForm((prev) => ({ ...prev, holidayCountry: "GB" }));
    }
  }, [countries, form.holidayCountry]);

  const inviteEmails = [
    ...new Set(form.invites.map((e) => e.trim().toLowerCase()).filter(Boolean)),
  ];

  // Returns an error message for the current step, or "" if it is complete.
  const validate = () => {
    if (step === 0) {
      if (form.name.trim().split(/\s+/).filter(Boolean).length < 2) return "Enter your first and last name.";
      if (!EMAIL_RE.test(form.email.trim())) return "Enter a valid work email.";
      if (!PASSWORD_RULES.every((r) => r.test(form.password))) return "Your password doesn't meet all the rules yet.";
    }
    if (step === 1) {
      if (!form.companyName.trim()) return "Enter your company name.";
      if (!form.teamSize) return "Choose your team size.";
      if (form.billingMarket === "AU" && !form.billingRegion) return "Choose your state or territory.";
    }
    if (step === 2) {
      if (form.holidays && !form.holidayCountry) return "Choose the country for your public holidays.";
    }
    if (step === 3) {
      const bad = inviteEmails.find((e) => !EMAIL_RE.test(e));
      if (bad) return `"${bad}" isn't a valid email address.`;
      if (inviteEmails.includes(form.email.trim().toLowerCase())) return "You don't need to invite yourself.";
    }
    return "";
  };

  const back = () => {
    setError("");
    if (step === 0) navigate("/get-started");
    else setStep(step - 1);
  };

  // Leave defaults use the same endpoints as the Settings page. They run
  // after the company exists; if one fails the company is still created.
  // Public holidays: create a holiday location for the chosen country (and
  // region), import its holidays and make it the company default. Falls back
  // to the old UK import if locations aren't available.
  const setUpHolidays = async () => {
    if (!form.holidays || !form.holidayCountry) return;
    try {
      const created = await apiService.createHolidayLocation({
        countryCode: form.holidayCountry,
        region: form.holidayRegion || null,
      });
      const location = created?.data ?? created;
      if (location?.id) {
        await apiService.importLocationHolidays(location.id, HOLIDAY_YEARS);
        await apiService.setDefaultHolidayLocation(location.id).catch(() => {});
      }
    } catch (err) {
      if (form.holidayCountry === "GB") {
        await apiService.importUKPublicHolidays(new Date().getFullYear());
      } else {
        throw err;
      }
    }
  };

  const applyDefaults = async () => {
    const results = await Promise.allSettled([
      apiService.updateCompanySettings({
        leaveYearStartMonth: Number(form.startMonth),
        leaveYearStartDay: 1,
        defaultAnnualLeaveDays: clamp(form.annualDays, 0, 365),
        workingDaysPerWeek: clamp(form.workingDays, 1, 7),
        carryForwardDaysMax: clamp(form.carryOver, 0, 365),
      }),
      setUpHolidays(),
    ]);
    return results.every((r) => r.status === "fulfilled");
  };

  const create = async () => {
    setSubmitting(true);
    const email = form.email.trim().toLowerCase();
    const companyName = form.companyName.trim();

    try {
      await apiService.register({
        name: form.name.trim(),
        email,
        password: form.password,
        companyName,
        teamSize: form.teamSize,
        emails: inviteEmails,
        // "Another country" is billed on the UK price list; we still record the actual country
        billingCountry: form.billingMarket === "OTHER" ? form.holidayCountry : form.billingMarket,
        region: form.billingMarket === "AU"
          ? form.billingRegion
          : form.billingMarket === form.holidayCountry ? form.holidayRegion || null : null,
        referralCode: form.referralCode.trim() || undefined,
      });

      // No session came back: sign in normally. If that is refused,
      // the email needs verifying first.
      if (!tokenManager.isAuthenticated()) {
        try {
          await apiService.login(email, form.password);
        } catch (loginError) {
          setNeedsVerify(true);
          return;
        }
      }

      const allSaved = await applyDefaults();
      if (allSaved) {
        toast.success(`${companyName} is set up — your 30-day free trial has started.`);
      } else {
        toast(`${companyName} was created, but some leave defaults didn't save. Check them in Settings.`, {
          duration: 7000,
        });
      }

      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          (err?.response
            ? "The company couldn't be created. Try again."
            : "Couldn't reach the server. Check your connection and try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;

    const message = validate();
    if (message) {
      setError(message);
      return;
    }

    if (step < STEPS - 1) {
      setError("");
      setStep(step + 1);
    } else {
      create();
    }
  };

  if (needsVerify) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`${form.companyName.trim()} is registered. We've sent a link to ${form.email.trim()} to confirm it's you.`}
      >
        <div className="flex justify-center mb-6 text-[#1C1A14]">
          <MailCheck size={36} />
        </div>
        <button type="button" className={primaryButtonClass} onClick={() => navigate("/login")}>
          Go to sign in
        </button>
      </AuthShell>
    );
  }

  const submitLabel =
    step < STEPS - 1
      ? "Continue"
      : inviteEmails.length
        ? "Create company and send invites"
        : "Create company";

  return (
    <AuthShell
      title={TITLES[step].title}
      subtitle={TITLES[step].subtitle}
      step={step}
      steps={STEPS}
      onBack={submitting ? undefined : back}
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {step === 0 && (
          <>
            <Field label="Full name">
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className={inputClass}
                placeholder="Jane Smith"
                autoComplete="name"
                autoFocus
              />
            </Field>
            <Field label="Work email">
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={inputClass}
                placeholder="jane@company.com"
                autoComplete="email"
              />
            </Field>
            <Field label="Password">
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  className={`${inputClass} pr-12`}
                  placeholder="Create a password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 w-12 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <ul className="mt-3 space-y-1.5">
                {PASSWORD_RULES.map((rule) => {
                  const ok = rule.test(form.password);
                  return (
                    <li key={rule.label} className={`flex items-center gap-2 text-xs ${ok ? "text-green-700" : "text-gray-500"}`}>
                      <span className={`h-4 w-4 flex items-center justify-center rounded-full border ${ok ? "bg-green-600 border-green-600 text-white" : "border-gray-300"}`}>
                        {ok ? <Check size={10} /> : null}
                      </span>
                      {rule.label}
                    </li>
                  );
                })}
              </ul>
            </Field>
          </>
        )}

        {step === 1 && (
          <>
            <Field label="Company name">
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => set("companyName", e.target.value)}
                className={inputClass}
                placeholder="Your company"
                autoComplete="organization"
                autoFocus
              />
            </Field>
            <Field label="Team size">
              <div className="flex flex-wrap gap-2">
                {TEAM_SIZES.map((size) => {
                  const selected = form.teamSize === size;
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => set("teamSize", size)}
                      aria-pressed={selected}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        selected
                          ? "bg-[#1C1A14] border-[#1C1A14] text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Where is the company based?" hint="Sets your currency and local defaults for leave and public holidays.">
              <select
                value={form.billingMarket}
                onChange={(e) => setMarket(e.target.value)}
                className={inputClass}
                aria-label="Billing country"
              >
                {BILLING_MARKETS.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.code === "OTHER" ? "🌍" : flagEmoji(m.code)}  {m.name}
                  </option>
                ))}
              </select>
            </Field>
            {form.billingMarket === "AU" && (
              <Field label="State or territory" hint="Picks your public holidays and timezone.">
                <select
                  value={form.billingRegion}
                  onChange={(e) => {
                    const region = e.target.value;
                    setError("");
                    setForm((prev) => ({ ...prev, billingRegion: region, holidayRegion: prev.holidayCountry === "AU" ? region : prev.holidayRegion }));
                  }}
                  className={inputClass}
                  aria-label="State or territory"
                >
                  <option value="">Choose…</option>
                  {AU_STATES.map((st) => (
                    <option key={st.code} value={st.code}>{st.name}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Partner or referral code (optional)">
              <input
                type="text"
                value={form.referralCode}
                onChange={(e) => set("referralCode", e.target.value.toUpperCase())}
                className={inputClass}
                placeholder="e.g. PARTNER10"
                autoComplete="off"
              />
            </Field>
            <p className="text-xs text-gray-500">
              Every company starts with a 30-day free trial of everything. No card needed.
            </p>
          </>
        )}

        {step === 2 && (
          <>
            <NumberField
              label="Annual leave allowance"
              hint="Days per year for a full-time employee."
              value={form.annualDays}
              min={0}
              max={365}
              onChange={(v) => set("annualDays", v)}
            />
            {MARKET_LEAVE_NOTES[form.billingMarket] && (
              <p className="text-xs text-gray-500 -mt-2">
                {MARKET_LEAVE_NOTES[form.billingMarket]} You can fine-tune this in Settings → Leave policies.
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <NumberField
                label="Working week"
                hint="Days per week."
                value={form.workingDays}
                min={1}
                max={7}
                onChange={(v) => set("workingDays", v)}
              />
              <NumberField
                label="Carry over"
                hint="Max unused days."
                value={form.carryOver}
                min={0}
                max={365}
                onChange={(v) => set("carryOver", v)}
              />
            </div>
            <Field label="Leave year starts">
              <select
                value={form.startMonth}
                onChange={(e) => set("startMonth", Number(e.target.value))}
                className={inputClass}
              >
                {MONTHS.map((month, i) => (
                  <option key={month} value={i + 1}>
                    1 {month}
                  </option>
                ))}
              </select>
            </Field>
            <div className="rounded-[14px] border border-[#E3D6B6] bg-white p-4 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.holidays}
                  onChange={(e) => set("holidays", e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-400 accent-[#1C1A14]"
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-800">Add public holidays</span>
                  <span className="block text-xs text-gray-500">
                    Imports {HOLIDAY_YEARS[0]} and {HOLIDAY_YEARS[1]} {form.billingMarket === 'GB' ? 'bank' : 'public'} holidays for your country into the calendar.
                  </span>
                </span>
              </label>

              {form.holidays && (
                <div className="space-y-4">
                  <Field label="Country">
                    <select
                      value={form.holidayCountry}
                      onChange={(e) => set("holidayCountry", e.target.value)}
                      className={inputClass}
                      aria-label="Country for public holidays"
                    >
                      {countries.map((c) => (
                        <option key={c.code} value={c.code}>
                          {flagEmoji(c.code)}  {c.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {regions.length > 0 && (
                    <Field label="Region">
                      <select
                        value={form.holidayRegion}
                        onChange={(e) => set("holidayRegion", e.target.value)}
                        className={inputClass}
                        disabled={loadingRegions}
                        aria-label="Region for public holidays"
                      >
                        <option value="">Whole country</option>
                        {regions.map((r) => (
                          <option key={r.code} value={r.code}>{r.name}</option>
                        ))}
                      </select>
                    </Field>
                  )}
                </div>
              )}
              {form.holidays && (
                <p className="text-xs text-gray-500 m-0">
                  You can add more countries later in Settings → Public holidays.
                </p>
              )}
            </div>
            <p className="text-xs text-gray-500">
              The standard leave types (holiday, sickness and so on) are created for you.
            </p>
          </>
        )}

        {step === 3 && (
          <div className="space-y-3">
            {form.invites.map((value, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="email"
                  value={value}
                  onChange={(e) =>
                    set("invites", form.invites.map((v, j) => (j === i ? e.target.value : v)))
                  }
                  className={inputClass}
                  placeholder="colleague@company.com"
                  aria-label={`Colleague ${i + 1} email`}
                  disabled={submitting}
                  autoFocus={i === 0}
                />
                {form.invites.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => set("invites", form.invites.filter((_, j) => j !== i))}
                    aria-label={`Remove colleague ${i + 1}`}
                    disabled={submitting}
                    className="h-10 w-10 shrink-0 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
                  >
                    <X size={18} />
                  </button>
                ) : null}
              </div>
            ))}
            {form.invites.length < MAX_INVITES ? (
              <button
                type="button"
                onClick={() => set("invites", [...form.invites, ""])}
                disabled={submitting}
                className="flex items-center gap-2 text-sm font-medium text-[#1C1A14] hover:text-[#0f0e0a]"
              >
                <Plus size={16} />
                Add another person
              </button>
            ) : null}
          </div>
        )}

        <FormError>{error}</FormError>

        <button type="submit" disabled={submitting} className={primaryButtonClass}>
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Creating your company...
            </span>
          ) : (
            submitLabel
          )}
        </button>
      </form>

      {step === 0 ? (
        <p className="mt-6 text-sm text-center text-gray-600">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[#1C1A14] hover:text-[#0f0e0a]">
            Sign in
          </Link>
        </p>
      ) : null}
    </AuthShell>
  );
};

export default Signup;